import { converter, toGamut } from 'culori'

import { copyFill, copyStroke } from '../copy'
import { readAllPluginData, withAllPluginData } from '../plugin-data/field'
import { OPEN_PENCIL_PLUGIN_DATA } from '../plugin-data/fields'
import type { Color } from '../primitives'
import type { PluginDataEntry, SceneNode } from '../types'
import { normalizeColor } from './normalize'

export interface OkHCLColor {
  h: number
  c: number
  l: number
  a?: number
}

export interface OkHCLPayload {
  version: 1
  kind: 'fill' | 'stroke'
  index: number
  color: OkHCLColor
}

const toRGB = converter('rgb')
const toP3 = converter('p3')
const toOkHCL = converter('oklch')
const toDisplayableRGB = toGamut('rgb', 'oklch')
const toDisplayableP3 = toGamut('p3', 'oklch')

/** Colour spaces a document's stored numbers can be in. */
export type OkHCLColorSpace = 'srgb' | 'display-p3'

function clampUnit(value: number): number {
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

function normalizeHue(value: number): number {
  const hue = value % 360
  return hue < 0 ? hue + 360 : hue
}

function normalizeOkHCLColor(color: OkHCLColor): OkHCLColor {
  return {
    h: normalizeHue(color.h),
    c: Math.max(0, color.c),
    l: clampUnit(color.l),
    a: clampUnit(color.a ?? 1)
  }
}

/** Coordinates in `colorSpace`, so callers can store colours in the document's profile. */
export function okhclToRGBA(color: OkHCLColor, colorSpace: OkHCLColorSpace = 'srgb'): Color {
  const normalized = normalizeOkHCLColor(color)
  const oklch = {
    mode: 'oklch' as const,
    l: normalized.l,
    c: normalized.c,
    h: normalized.h,
    alpha: normalized.a
  }
  if (colorSpace === 'display-p3') {
    const p3 = toP3(toDisplayableP3(oklch))
    return normalizeColor({
      r: p3.r,
      g: p3.g,
      b: p3.b,
      a: p3.alpha ?? normalized.a
    })
  }
  const rgb = toRGB(toDisplayableRGB(oklch))
  return normalizeColor({
    r: rgb.r,
    g: rgb.g,
    b: rgb.b,
    a: rgb.alpha ?? normalized.a
  })
}

/** Reads `color` as coordinates in `colorSpace`, the inverse of {@link okhclToRGBA}. */
export function rgbaToOkHCL(color: Color, colorSpace: OkHCLColorSpace = 'srgb'): OkHCLColor {
  const oklch = toOkHCL(
    colorSpace === 'display-p3'
      ? { mode: 'p3', r: color.r, g: color.g, b: color.b, alpha: color.a }
      : { mode: 'rgb', r: color.r, g: color.g, b: color.b, alpha: color.a }
  )
  return normalizeOkHCLColor({
    h: oklch.h ?? 0,
    c: oklch.c,
    l: oklch.l,
    a: oklch.alpha ?? color.a
  })
}

export function serializeOkHCLPayload(payload: OkHCLPayload): string {
  return OPEN_PENCIL_PLUGIN_DATA.okhcl.encode(payload)
}

export function parseOkHCLPayload(value: string): OkHCLPayload | null {
  return OPEN_PENCIL_PLUGIN_DATA.okhcl.decode(value) ?? null
}

function createOkHCLPayload(
  kind: 'fill' | 'stroke',
  index: number,
  color: OkHCLColor
): OkHCLPayload {
  return {
    version: 1,
    kind,
    index,
    color: normalizeOkHCLColor(color)
  }
}

/** The node's plugin data with the payload for one paint replaced, or removed without `color`. */
function withOkHCLPayload(
  node: SceneNode,
  kind: 'fill' | 'stroke',
  index: number,
  color?: OkHCLColor
): PluginDataEntry[] {
  const payloads = getNodeOkHCLPayloads(node).filter(
    (payload) => payload.kind !== kind || payload.index !== index
  )
  if (color) payloads.push(createOkHCLPayload(kind, index, color))
  return withAllPluginData(node.pluginData, OPEN_PENCIL_PLUGIN_DATA.okhcl, payloads)
}

export function setNodeFillOkHCL(
  node: SceneNode,
  index: number,
  color: OkHCLColor,
  colorSpace: OkHCLColorSpace = 'srgb'
): Partial<SceneNode> {
  const fills = node.fills.map(copyFill)
  if (index < 0 || index >= fills.length) throw new Error(`Fill ${index} not found`)
  const fill = fills[index]
  const rgba = okhclToRGBA(color, colorSpace)
  fills[index] = {
    ...fill,
    color: rgba,
    opacity: rgba.a
  }

  return {
    fills,
    pluginData: withOkHCLPayload(node, 'fill', index, color)
  }
}

export function setNodeStrokeOkHCL(
  node: SceneNode,
  index: number,
  color: OkHCLColor,
  colorSpace: OkHCLColorSpace = 'srgb'
): Partial<SceneNode> {
  const strokes = node.strokes.map(copyStroke)
  if (index < 0 || index >= strokes.length) throw new Error(`Stroke ${index} not found`)
  const stroke = strokes[index]
  const rgba = okhclToRGBA(color, colorSpace)
  strokes[index] = {
    ...stroke,
    color: rgba,
    opacity: rgba.a
  }

  return {
    strokes,
    pluginData: withOkHCLPayload(node, 'stroke', index, color)
  }
}

export function clearNodeFillOkHCL(node: SceneNode, index: number): Partial<SceneNode> {
  return { pluginData: withOkHCLPayload(node, 'fill', index) }
}

export function clearNodeStrokeOkHCL(node: SceneNode, index: number): Partial<SceneNode> {
  return { pluginData: withOkHCLPayload(node, 'stroke', index) }
}

export function getNodeOkHCLPayloads(node: SceneNode): OkHCLPayload[] {
  return readAllPluginData(node.pluginData, OPEN_PENCIL_PLUGIN_DATA.okhcl)
}

export function getFillOkHCL(node: SceneNode, index: number): OkHCLPayload | null {
  return (
    getNodeOkHCLPayloads(node).find(
      (payload) => payload.kind === 'fill' && payload.index === index
    ) ?? null
  )
}

export function getStrokeOkHCL(node: SceneNode, index: number): OkHCLPayload | null {
  return (
    getNodeOkHCLPayloads(node).find(
      (payload) => payload.kind === 'stroke' && payload.index === index
    ) ?? null
  )
}
